import * as React from "react";

type Tab = { id: string; label: string };

type Props = {
  tabs: Tab[];
  activeIndex: number;
  onChange: (index: number) => void;
};

/**
 * Premium tab bar with a sliding coral underline, soft hover/press states,
 * and reduced-motion fallback. Routing is unchanged — this is presentation only.
 */
export default function AppTabBar({ tabs, activeIndex, onChange }: Props) {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const [indicator, setIndicator] = React.useState<{ left: number; width: number; ready: boolean }>({
    left: 0,
    width: 0,
    ready: false,
  });

  const measure = React.useCallback(() => {
    const list = listRef.current;
    const node = itemRefs.current[activeIndex];
    if (!list || !node) return;
    const listRect = list.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    // Underline sits under the label text, not the full button padding.
    const pad = 14;
    const width = Math.max(20, nodeRect.width - pad * 2);
    const left = nodeRect.left - listRect.left + list.scrollLeft + (nodeRect.width - width) / 2;
    setIndicator({ left, width, ready: true });
  }, [activeIndex]);

  React.useLayoutEffect(() => {
    measure();
  }, [measure, tabs.length]);

  React.useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  // Keep the active tab visible when overflowing horizontally on small screens.
  React.useEffect(() => {
    const node = itemRefs.current[activeIndex];
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeIndex]);

  return (
    <div
      ref={listRef}
      role="tablist"
      style={{
        position: "relative",
        display: "flex",
        borderBottom: "1px solid #F3D7DF",
        marginBottom: 20,
        overflowX: "auto",
        background: "#FBF7F4",
        padding: "4px 4px 0",
        gap: 2,
        scrollbarWidth: "none",
      }}
    >
      {tabs.map((t, i) => {
        const active = i === activeIndex;
        return (
          <button
            key={t.id}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(i)}
            className="km-tab-btn"
            data-active={active ? "true" : "false"}
            style={{
              position: "relative",
              flex: "1 0 auto",
              minWidth: 64,
              padding: "13px 14px 14px",
              background: "transparent",
              border: "none",
              color: active ? "#2D0E43" : "#6E4B73",
              fontFamily: active
                ? '"Gloock","Instrument Serif",serif'
                : '"Work Sans",system-ui,sans-serif',
              fontWeight: active ? 600 : 500,
              fontSize: active ? 14 : 12.5,
              letterSpacing: active ? "-0.01em" : "0.02em",
              cursor: "pointer",
              whiteSpace: "nowrap",
              borderRadius: "12px 12px 0 0",
              outline: "none",
              transition:
                "color 220ms cubic-bezier(0.22,1,0.36,1), font-size 220ms cubic-bezier(0.22,1,0.36,1), background-color 180ms ease, transform 140ms ease",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {t.label}
          </button>
        );
      })}
      <span
        aria-hidden
        style={{
          position: "absolute",
          bottom: -1,
          left: 0,
          height: 2.5,
          width: indicator.width,
          transform: `translateX(${indicator.left}px)`,
          background: "linear-gradient(90deg, #F34E54 0%, #FF7A8A 100%)",
          borderRadius: 999,
          boxShadow: "0 4px 10px -4px rgba(243,78,84,0.55)",
          opacity: indicator.ready ? 1 : 0,
          transition:
            "transform 320ms cubic-bezier(0.22,1,0.36,1), width 320ms cubic-bezier(0.22,1,0.36,1), opacity 200ms ease",
          pointerEvents: "none",
          willChange: "transform,width",
        }}
      />
      <style>{`
        .km-tab-btn::-webkit-scrollbar { display: none; }
        .km-tab-btn:hover { color: #2D0E43 !important; background-color: rgba(253,236,239,0.55) !important; }
        .km-tab-btn:active { transform: scale(0.97); }
        .km-tab-btn:focus-visible { box-shadow: 0 0 0 3px rgba(243,78,84,0.28); }
        .km-tab-btn[data-active="true"] { background-color: rgba(253,236,239,0.6) !important; }
        @media (prefers-reduced-motion: reduce) {
          .km-tab-btn, .km-tab-btn * { transition: none !important; }
        }
      `}</style>
    </div>
  );
}
